/**
 * SF-132: shared percent↔acre math for the Buildings page — extracted from what was previously
 * duplicated between `BuildingRow.tsx`'s inline deficit calc and `Buildings.tsx`'s
 * `handleFillDeficit`. Single source of truth for `BuildingRow.tsx`, `Buildings.tsx`, and
 * `MobileBuildingsShell`'s stepper.
 */

export function acresForPct(totalLand: number, pct: number): number {
  if (totalLand <= 0) return 0;
  return Math.floor((totalLand * Math.max(0, pct)) / 100);
}

export function pctForAcres(totalLand: number, acres: number): number {
  if (totalLand <= 0) return 0;
  return (acres / totalLand) * 100;
}

export interface ComputeDeficitParams {
  targetPct: number | null;
  totalLand: number;
  owned: number;
  inProgress: number;
  remainingBarren: number;
}

export interface ComputeDeficitResult {
  deficit: number;
  fillQty: number;
}

export function computeDeficit({
  targetPct,
  totalLand,
  owned,
  inProgress,
  remainingBarren,
}: ComputeDeficitParams): ComputeDeficitResult {
  if (targetPct === null || targetPct <= 0) {
    return { deficit: 0, fillQty: 0 };
  }
  const target = acresForPct(totalLand, targetPct);
  const current = owned + inProgress;
  const deficit = Math.max(0, target - current);
  const fillQty = Math.min(deficit, Math.max(0, remainingBarren));
  return { deficit, fillQty };
}

/**
 * SF-143: acres-driven counterpart to computeDeficit, backing the mobile live-commit stepper.
 * Percent never enters this computation — acres/barren/buildQty are already exact integers, so
 * routing the write path through percent (as v1.0 did) is what caused the seed-rounding
 * round-trip data loss this function exists to eliminate.
 */
export interface ResolveTargetQtyParams {
  targetAcres: number;
  owned: number;
  inProgress: number;
  remainingBarren: number;
}

export interface ResolveTargetQtyResult {
  desiredDelta: number; // acres needed to reach targetAcres, before barren capping (>= 0)
  newQty: number;       // desiredDelta capped to remainingBarren — what buildQty[id] should become
}

export function resolveTargetQty({ targetAcres, owned, inProgress, remainingBarren }: ResolveTargetQtyParams): ResolveTargetQtyResult {
  const current = owned + inProgress;
  const desiredDelta = Math.max(0, targetAcres - current);
  const newQty = Math.min(desiredDelta, Math.max(0, remainingBarren));
  return { desiredDelta, newQty };
}

/**
 * SF-165 Component 3: acres-driven counterpart to resolveTargetQty, but for Raze's opposite
 * direction. Unlike Build, Raze has no cross-row barren-budget pool to clamp against — a row's
 * only ceiling is its own `owned` value — so `targetAcres` (the desired REMAINING owned acres
 * after razing) is clamped directly, with nothing left over to distinguish a "before capping"
 * value from an "after capping" value (Phase 3 Decision 3 — the v1.0 two-field
 * `ResolveRazeQtyResult` shape was dropped as degenerate).
 */
export interface ResolveRazeQtyParams {
  targetAcres: number; // desired REMAINING owned acres after razing
  owned: number;
}

export function resolveRazeQty({ targetAcres, owned }: ResolveRazeQtyParams): number {
  const clampedTarget = Math.max(0, Math.min(targetAcres, owned));
  return owned - clampedTarget; // always in [0, owned] by construction
}

/**
 * SF-165 Component 3: acres owned above a strategy target — backs the stepper's per-row
 * "⚡ Strategy (N%)" snap-down chip. Phase 3 Decision 4: deliberately does NOT early-return for
 * `targetPct <= 0` (unlike computeDeficit) — both live desktop raze paths (RazePanel.tsx's
 * per-row ⚡ guard, computeAutoRaze) already treat a 0% strategy target as "raze everything," so
 * matching that here is parity, not a new behavior. Only `targetPct === null` (no target set at
 * all) short-circuits to zero surplus. `acresForPct` already floors a negative pct at 0 internally,
 * so no additional clamp is needed for a (currently unreachable) negative target.
 */
export interface ComputeRazeSurplusParams {
  targetPct: number | null;
  totalLand: number;
  owned: number;
}

export interface ComputeRazeSurplusResult {
  surplus: number; // acres owned above the strategy target, 0 if at/below target or no target
}

export function computeRazeSurplus({ targetPct, totalLand, owned }: ComputeRazeSurplusParams): ComputeRazeSurplusResult {
  if (targetPct === null) return { surplus: 0 };
  const target = acresForPct(totalLand, targetPct);
  return { surplus: Math.max(0, owned - target) };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * SF-190 Component 3: one `+1%`/`+2%`/`+`/`−` tap on Build's mobile stepper (`MobileBuildingsShell.
 * adjustStepper`), operating on the Add delta (`stepper.addAcres`), not a resulting total.
 *
 * Real bug this replaces: the pre-SF-190 stepper added a *fixed acre amount*, floor-computed once
 * from `totalLand` (`acresForPct(totalLand, 1)`), on every tap — floor error compounds across taps
 * (10x `+1%` on a 333-acre province landed at 9.0%, not 10%). Naively "fixing" this by round-tripping
 * through the raw (un-rounded) acre value is algebraically identical to the bug (substituting
 * `pctForAcres`/`acresForPct`'s bodies collapses back to `currentAddAcres + acresForPct(totalLand,
 * deltaPct)`, the same fixed-delta accumulation).
 *
 * The actual fix: quantize the *current* percent to its displayed integer value before adding, so
 * every tap's error is bounded to at most one round/floor unit and never compounds (Phase 3
 * Decision 1 — see SF190_PHASE3_CONSOLIDATION.md for the full 10-tap numeric proof).
 */
export interface StepAddByPctParams {
  totalLand: number;
  currentAddAcres: number;
  deltaPct: number; // e.g. +1, +2, -1
  maxAddAcres: number;
}

export function stepAddByPct({ totalLand, currentAddAcres, deltaPct, maxAddAcres }: StepAddByPctParams): number {
  // Explicit guard, not just inherited from pctForAcres/acresForPct's own totalLand<=0 internal
  // guards: without this, the direction-symmetric step floor below (Math.max(rawNext,
  // currentAddAcres + 1)) would make a positive tap keep incrementing by 1 acre per tap even at
  // totalLand<=0 — rawNext is pinned to 0 by the inner guards, but currentAddAcres + 1 still wins
  // the max() every time, so the function would NOT degrade to always returning 0 without this.
  if (totalLand <= 0) return 0;
  const currentAddPct = Math.round(pctForAcres(totalLand, currentAddAcres));
  const targetPct = currentAddPct + deltaPct;
  const rawNext = acresForPct(totalLand, targetPct);
  // Direction-symmetric step floor: guarantees every tap moves at least 1 acre in the direction of
  // deltaPct whenever there's room to move (replaces a one-sided Math.max(1, …), which would be
  // asymmetric across +/- taps).
  const stepped = deltaPct > 0
    ? Math.max(rawNext, currentAddAcres + 1)
    : Math.min(rawNext, currentAddAcres - 1);
  return clamp(stepped, 0, maxAddAcres);
}
